import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../AuthContext';
import { canFullAccessVendor, canSeeVendorPackPrice, canContactVendor, canSeeVendorPricing, canTogglePreferredVendor, canLockVendor, isStaffWithPurchasing } from '../../lib/permissions';
import { IconStar, IconLock, IconExternalLink, IconPlus, IconAlertTriangle } from '@tabler/icons-react';
import UnitSelect from '../UnitSelect';

/**
 * VendorItemsPanel
 * Shared component — used by InventoryItemDetail (right column).
 * VendorCatalogPage.js maintains its own separate, parallel vendor list
 * implementation, not this component — see Issue #23.
 *
 * Props:
 *   itemId            — bigint, the inventory_items.id
 *   itemName          — string, display name
 *   itemUom           — string, unit of measure
 *   isMobile          — boolean
 *   setActive         — function to navigate
 *   setFilterItemId   — function to set filter context in App.js before navigating
 *   setFilterItemName — function to set filter name in App.js before navigating
 *   readOnly          — boolean, suppresses all edit controls (default false)
 */

// #393 door 3, Session 78n2, and it was a live data-corruption bug rather than
// the labelling inconsistency the issue described.
//
// This function was named calcUnitCost and MULTIPLIED - it computed a pack total
// from a per-unit price. openEdit() meanwhile pre-filled the field from
// vi.vendor_price, which is the PACK price. So opening a priced row put 400 (a
// pallet price) into a box labelled "Unit price", the preview read "Pack total
// (saved) = $20000.00", and SAVING WITHOUT CHANGING ANYTHING wrote $20,000.
// Twice through and it is a million. Reproduced live on staging against MDO 4x8
// before this was touched.
//
// Now it divides, like the two Vendor Catalog forms and like the database: the
// field is what the vendor charges for one pack, and unit cost is derived.
function derivedUnitCost(packPrice, packQty) {
  const price = parseFloat(packPrice);
  const qty = parseInt(packQty, 10);
  if (!isNaN(price) && !isNaN(qty) && qty > 0) {
    return (price / qty).toFixed(2);
  }
  return null;
}

export default function VendorItemsPanel({
  itemId,
  itemName,
  itemUom,
  isMobile,
  setActive,
  setFilterItemId,
  setFilterItemName,
  readOnly = false,
}) {
  const { profile, isAdmin, isManager, isExecutive, isPurchasing, isTeamMember } = useAuth();

  const [settings, setSettings] = useState(null);
  const [vendorItems, setVendorItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editVendorPrice, setEditVendorPrice] = useState('');
  const [editPackQty, setEditPackQty] = useState('');
  const [editPackUnit, setEditPackUnit] = useState('');
  const [saving, setSaving] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await supabase.from('company_settings').select('*').single();
      setSettings(data || {});
    } catch {
      setSettings({});
    }
  }, []);

  const load = useCallback(async () => {
    if (!itemId) return;
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase
        .from('vendor_items')
        .select(`
          id, vendor_id, vendor_item_number, vendor_price, unit_cost, price_date,
          is_preferred, price_locked, locked_by, pack_qty, pack_unit,
          inventory_vendors ( id, name, contact_name, phone, email, is_active )
        `)
        .eq('item_id', itemId)
        .order('is_preferred', { ascending: false });
      if (err) throw err;
      setVendorItems(data || []);
    } catch {
      setError('Could not load vendor data.');
    } finally {
      setLoading(false);
    }
  }, [itemId]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    load();
  }, [load]);

  const canFullAccess = canFullAccessVendor({ isAdmin, isManager, isExecutive, isPurchasing, profile }, settings);
  const canContact    = canContactVendor({ isAdmin, isManager, isExecutive, isPurchasing, isTeamMember, profile }, settings);
  const canTogglePreferred = canTogglePreferredVendor({ isAdmin, isManager, isExecutive, isPurchasing, profile });
  const canLock       = canLockVendor({ isAdmin, isManager, isExecutive, isPurchasing, profile });
  const canSeePricing = canSeeVendorPricing({ isAdmin, isManager, isExecutive, isPurchasing, profile });
  const canSeePackPrice = canSeeVendorPackPrice({ isAdmin, isManager, isExecutive, isPurchasing, profile }, settings);
  const canViewVendorInfo = isStaffWithPurchasing({ isAdmin, isManager, isExecutive, isPurchasing });

  function navigateToVendorCatalog() {
    if (setFilterItemId) setFilterItemId(itemId);
    if (setFilterItemName) setFilterItemName(itemName);
    setActive('purchasing_vendors');
  }

  function openEdit(vi) {
    setEditingId(vi.id);
    setEditVendorPrice(vi.vendor_price != null ? String(vi.vendor_price) : '');
    setEditPackQty(vi.pack_qty != null ? String(vi.pack_qty) : '');
    setEditPackUnit(vi.pack_unit || '');
  }

  function closeEdit() {
    setEditingId(null);
    setEditVendorPrice('');
    setEditPackQty('');
    setEditPackUnit('');
  }

  async function handleSavePrice(vi) {
    if (vi.price_locked) return;
    setSaving(true);
    try {
      const enteredPrice = parseFloat(editVendorPrice);
      const qtyEntered = parseInt(editPackQty, 10);
      const priceOk = !isNaN(enteredPrice) && enteredPrice > 0;
      const qtyOk = !isNaN(qtyEntered) && qtyEntered > 0;
      const unit = editPackUnit;
      // Stored AS TYPED. The multiply that used to live here is what turned a
      // $400 pallet price into $20,000 on a save that changed nothing.
      const vendorPrice = priceOk ? enteredPrice : null;
      if (priceOk && !qtyOk) {
        setError('Enter how many ' + uom + ' come in one pack before saving a price.');
        setSaving(false);
        return;
      }
      if (priceOk && !unit) {
        setError('Pick what the vendor sells this by before saving a price.');
        setSaving(false);
        return;
      }
      const qty = qtyOk ? qtyEntered : 1;

      await supabase
        .from('vendor_items')
        .update({
          vendor_price: vendorPrice,
          pack_qty:     qty,
          pack_unit:    unit || null,
          price_date:   new Date().toISOString(),
        })
        .eq('id', vi.id);

      closeEdit();
      await load();
    } catch {
      setError('Could not save price.');
    } finally {
      setSaving(false);
    }
  }

  async function handleTogglePreferred(vi) {
    if (!canTogglePreferred || readOnly) return;
    setSaving(true);
    try {
      if (!vi.is_preferred) {
        const others = vendorItems.filter(v => v.id !== vi.id && v.is_preferred);
        for (const other of others) {
          await supabase.from('vendor_items').update({ is_preferred: false }).eq('id', other.id);
        }
      }
      await supabase
        .from('vendor_items')
        .update({ is_preferred: !vi.is_preferred })
        .eq('id', vi.id);
      await load();
    } catch {
      setError('Could not update preferred status.');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleLock(vi) {
    if (!canLock || readOnly) return;
    setSaving(true);
    try {
      await supabase
        .from('vendor_items')
        .update({
          price_locked: !vi.price_locked,
          locked_by:    vi.price_locked ? null : profile?.id,
        })
        .eq('id', vi.id);
      await load();
    } catch {
      setError('Could not update lock status.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div style={{ padding: '12px 0', fontSize: 12, color: '#9A9A9A' }}>
        Loading vendors...
      </div>
    );
  }

  const activeVendorItems = vendorItems.filter(vi => vi.inventory_vendors?.is_active !== false);
  const preferred = activeVendorItems.find(vi => vi.is_preferred);
  // Was 'ea', a spelling no list in the app has ever contained - so this panel
  // could label a price for a unit the catalog cannot produce. 'unit' is a plain
  // English word rather than a fake unit code. #398's display-fallback half.
  const uom = itemUom || 'unit';

  return (
    <div>
      {/* Header row */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 10,
      }}>
        <div style={{
          fontSize: 11,
          fontWeight: 700,
          color: 'var(--gray-mid, #7A7A7A)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}>
          Vendors for this item
        </div>
        {!readOnly && setActive && canFullAccess && (
          <button
            onClick={navigateToVendorCatalog}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              background: 'none',
              border: 'none',
              fontSize: 11,
              color: '#1D9E75',
              cursor: 'pointer',
              fontWeight: 600,
              padding: 0,
            }}
          >
            <IconExternalLink size={12} />
            Manage in vendor catalog
          </button>
        )}
      </div>

      {error && (
        <div style={{
          background: '#FFEBEE',
          color: '#A32D2D',
          borderRadius: 8,
          padding: '8px 10px',
          fontSize: 11,
          marginBottom: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}>
          <IconAlertTriangle size={13} />
          {error}
        </div>
      )}

      {activeVendorItems.length === 0 ? (
        <div style={{
          background: '#F5F5F3',
          borderRadius: 8,
          padding: '12px 14px',
          fontSize: 12,
          color: '#9A9A9A',
          fontStyle: 'italic',
          marginBottom: 8,
        }}>
          No vendors associated with this item yet.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
          {activeVendorItems.map(vi => {
            const v           = vi.inventory_vendors;
            const isPreferred = vi.is_preferred;
            const isLocked    = vi.price_locked;
            const hasUnitCost = vi.unit_cost != null;
            const hasItemNum  = !!vi.vendor_item_number;
            const isEditingThis = editingId === vi.id;

            const computedCost = derivedUnitCost(editVendorPrice, editPackQty);
            // Always shown when it CAN be, including at pack qty 1 - that is the
            // case it most needs to state out loud. It used to hide itself
            // exactly when the numbers were least obvious.
            const showCalcPill = computedCost !== null;
            const priceTyped = parseFloat(editVendorPrice) > 0;
            const editBlocked = priceTyped && (!(parseInt(editPackQty, 10) > 0) || !editPackUnit);

            return (
              <div
                key={vi.id}
                style={{
                  background: isPreferred ? '#F0FFF8' : 'white',
                  border: `1px solid ${isPreferred ? '#5DCAA5' : '#E8E8E8'}`,
                  borderRadius: 8,
                  padding: isMobile ? '10px 12px' : '10px 14px',
                }}
              >
                {/* ── Vendor name / badges row ── */}
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  marginBottom: 4,
                  gap: 8,
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0F0F0F', marginBottom: 1 }}>
                      {v?.name || '—'}
                    </div>
                    {canContact && v?.contact_name && (
                      <div style={{ fontSize: 11, color: '#7A7A7A', marginBottom: 1 }}>
                        {v.contact_name}
                      </div>
                    )}
                    {canViewVendorInfo && (
                      <div style={{ fontSize: 11, color: hasItemNum ? '#5C5C5C' : '#B0B0B0', fontStyle: hasItemNum ? 'normal' : 'italic', marginBottom: 1 }}>
                        {hasItemNum
                          ? <>Item # <strong style={{ color: '#0F0F0F' }}>{vi.vendor_item_number}</strong></>
                          : 'No item # on file'
                        }
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 4, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {isPreferred && (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 3,
                        fontSize: 10, fontWeight: 700,
                        background: '#E1F5EE', color: '#085041',
                        borderRadius: 4, padding: '2px 7px',
                      }}>
                        <IconStar size={9} />
                        Preferred
                      </span>
                    )}
                    {isLocked && canSeePricing && (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 3,
                        fontSize: 10, fontWeight: 700,
                        background: '#EEEDFE', color: '#3C3489',
                        borderRadius: 4, padding: '2px 7px',
                      }}>
                        <IconLock size={9} />
                        Contract
                      </span>
                    )}
                  </div>
                </div>

                {/* ── team_member: no-access box ── */}
                {isTeamMember && (
                  <div style={{
                    background: '#F5F5F3',
                    borderRadius: 6,
                    padding: '7px 10px',
                    fontSize: 11,
                    color: '#9A9A9A',
                    fontStyle: 'italic',
                    marginTop: 4,
                  }}>
                    No price or contact access
                  </div>
                )}

                {/* ── Pricing block (non-team_member) ── */}
                {canSeePricing && (
                  <>
                    {isEditingThis ? (
                      /* ── Inline edit panel ── */
                      <div style={{
                        background: '#F8FFFE',
                        border: '1px solid #1D9E75',
                        borderRadius: 8,
                        padding: '10px 12px',
                        marginTop: 6,
                        marginBottom: 6,
                      }}>
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <span style={{ fontSize: 10, color: '#9A9A9A' }}>$ per pack</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <span style={{ fontSize: 11, color: '#7A7A7A' }}>$</span>
                              <input
                                type="number"
                                step="0.01"
                                value={editVendorPrice}
                                onChange={e => setEditVendorPrice(e.target.value)}
                                autoFocus
                                style={{
                                  width: 80, height: 28,
                                  border: '1px solid #1D9E75', borderRadius: 6,
                                  padding: '0 8px', color: '#0F0F0F',
                                }}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') handleSavePrice(vi);
                                  if (e.key === 'Escape') closeEdit();
                                }}
                              />
                              <span style={{ fontSize: 11, color: '#7A7A7A' }}>/{editPackUnit || 'pack'}</span>
                            </div>
                          </div>

                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <span style={{ fontSize: 10, color: '#9A9A9A' }}>Units per pack</span>
                            <input
                              type="number"
                              step="1"
                              min="1"
                              value={editPackQty}
                              onChange={e => setEditPackQty(e.target.value)}
                              style={{
                                width: 56, height: 28,
                                border: '1px solid #D0D0D0', borderRadius: 6,
                                padding: '0 8px', color: '#0F0F0F',
                              }}
                              onKeyDown={e => {
                                if (e.key === 'Enter') handleSavePrice(vi);
                                if (e.key === 'Escape') closeEdit();
                              }}
                            />
                          </div>

                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <span style={{ fontSize: 10, color: '#9A9A9A' }}>Sells by</span>
                            <div style={{ width: 108 }}>
                              <UnitSelect
                                kind="package"
                                value={editPackUnit}
                                onChange={setEditPackUnit}
                                placeholder="pack type"
                                ariaLabel="What the vendor sells this by"
                                style={{ height: 28, fontSize: 12, borderRadius: 6, padding: '0 6px' }}
                              />
                            </div>
                          </div>

                          {showCalcPill && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                              <span style={{ fontSize: 10, color: '#9A9A9A' }}>Cost per {uom}</span>
                              <span style={{
                                display: 'inline-flex', alignItems: 'center',
                                background: '#E1F5EE', color: '#085041',
                                fontSize: 11, fontWeight: 700,
                                borderRadius: 4, padding: '4px 8px',
                                height: 28, boxSizing: 'border-box',
                              }}>
                                = ${computedCost} per {uom}
                              </span>
                            </div>
                          )}
                        </div>

                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            onClick={() => handleSavePrice(vi)}
                            disabled={saving || editBlocked}
                            title={editBlocked ? 'Say how many units are in one pack, and what the vendor sells it by.' : undefined}
                            style={{
                              padding: '4px 12px',
                              background: (saving || editBlocked) ? '#E0E0E0' : '#1D9E75',
                              color: (saving || editBlocked) ? '#9A9A9A' : 'white',
                              border: 'none', borderRadius: 6,
                              fontSize: 11, fontWeight: 600,
                              cursor: (saving || editBlocked) ? 'not-allowed' : 'pointer',
                            }}
                          >
                            {saving ? '...' : 'Save'}
                          </button>
                          <button
                            onClick={closeEdit}
                            style={{
                              padding: '4px 8px', background: 'white',
                              color: '#7A7A7A', border: '1px solid #E0E0E0',
                              borderRadius: 6, fontSize: 11, cursor: 'pointer',
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* ── Static price display ── */
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8, marginTop: 4 }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{
                              fontSize: 13, fontWeight: 700,
                              color: isLocked ? '#3C3489' : '#0F0F0F',
                            }}>
                              {hasUnitCost
                                ? `$${parseFloat(vi.unit_cost).toFixed(2)}/${uom}`
                                : <span style={{ fontSize: 11, color: '#9A9A9A', fontWeight: 400 }}>No price on file</span>
                              }
                            </span>
                            {!readOnly && canFullAccess && !isLocked && (
                              <button
                                onClick={() => openEdit(vi)}
                                style={{
                                  background: 'none', border: 'none',
                                  fontSize: 11, color: '#1D9E75',
                                  cursor: 'pointer', padding: 0,
                                  textDecoration: 'underline',
                                }}
                              >
                                {hasUnitCost ? 'Edit' : 'Add price'}
                              </button>
                            )}
                          </div>
                          {canSeePackPrice && vi.vendor_price != null && vi.pack_qty != null && vi.pack_unit && (
                            <div style={{ fontSize: 11, color: '#9A9A9A', marginTop: 2 }}>
                              ${parseFloat(vi.vendor_price).toFixed(2)}/{vi.pack_unit} · {vi.pack_qty} {uom}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* ── Action row ── */}
                    {!readOnly && !isEditingThis && (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                        {canContact && v?.phone && (
                          <a
                            href={`tel:${v.phone}`}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              padding: '4px 10px', background: '#0F0F0F', color: 'white',
                              borderRadius: 6, fontSize: 11, fontWeight: 600, textDecoration: 'none',
                            }}
                          >
                            Call {v.contact_name?.split(' ')[0] || ''}
                          </a>
                        )}
                        {canContact && v?.email && (
                          <a
                            href={`mailto:${v.email}`}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              padding: '4px 10px', background: 'white', color: '#0F0F0F',
                              border: '1px solid #E0E0E0', borderRadius: 6, fontSize: 11,
                              fontWeight: 600, textDecoration: 'none',
                            }}
                          >
                            Email
                          </a>
                        )}
                        {canTogglePreferred && (
                          <button
                            onClick={() => handleTogglePreferred(vi)}
                            disabled={saving}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              padding: '4px 10px',
                              background: isPreferred ? '#E1F5EE' : 'white',
                              color: isPreferred ? '#085041' : '#7A7A7A',
                              border: `1px solid ${isPreferred ? '#5DCAA5' : '#E0E0E0'}`,
                              borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                            }}
                          >
                            <IconStar size={11} />
                            {isPreferred ? 'Preferred' : 'Set preferred'}
                          </button>
                        )}
                        {canLock && (
                          <button
                            onClick={() => handleToggleLock(vi)}
                            disabled={saving}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              padding: '4px 10px',
                              background: isPreferred ? '#EEEDFE' : 'white',
                              color: isLocked ? '#3C3489' : '#7A7A7A',
                              border: `1px solid ${isLocked ? '#AFA9EC' : '#E0E0E0'}`,
                              borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                            }}
                          >
                            <IconLock size={11} />
                            {isLocked ? 'Locked' : 'Lock price'}
                          </button>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Associate vendor footer */}
      {!readOnly && setActive && canFullAccess && (
        <button
          onClick={navigateToVendorCatalog}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            width: '100%', padding: '8px 12px',
            background: 'none', border: '1px dashed #D0D0D0',
            borderRadius: 8, fontSize: 12, color: '#7A7A7A',
            cursor: 'pointer', justifyContent: 'center',
          }}
        >
          <IconPlus size={13} />
          Associate a vendor
        </button>
      )}

      {preferred && !isTeamMember && (
        <div style={{ marginTop: 8, fontSize: 10, color: '#9A9A9A', fontStyle: 'italic', textAlign: 'right' }}>
          Preferred vendor pre-fills purchasing orders
        </div>
      )}
    </div>
  );
}